export const AI_HUB_PATH = "/ai-hub";
export const AI_HUB_TAGGING_RESULT_PATH = "/ai-hub/tagging-result/:resultId";
export const AI_HUB_CONDITIONAL_TAGGING_RESULT_PATH =
  "/ai-hub/conditional-tagging-result/:resultId";
export const AI_HUB_OBJECT_ANALYSIS_PATH =
  "/ai-hub/object-analysis/:assessmentId";
export const AI_HUB_TRIAGE_RESULT_PATH = "/ai-hub/triage-result/:resultId";
export const AI_HUB_ASSESSMENT_PACK_PATH = "/ai-hub/assessment-packs/detail";

export type TaggingResultDetailRouteQuery = {
  objectType: string;
  objectSubType: string;
  version: string;
};

export function buildTaggingResultDetailSearch(
  query: TaggingResultDetailRouteQuery,
): string {
  const params = new URLSearchParams();
  const objectType = query.objectType.trim();
  const objectSubType = query.objectSubType.trim();
  const version = query.version.trim();
  if (objectType) params.set("objectType", objectType);
  if (objectSubType) params.set("subType", objectSubType);
  if (version) params.set("version", version);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function readTaggingResultDetailQuery(
  searchParams: URLSearchParams,
): TaggingResultDetailRouteQuery {
  return {
    objectType: searchParams.get("objectType")?.trim() ?? "",
    objectSubType: searchParams.get("subType")?.trim() ?? "",
    version: searchParams.get("version")?.trim() ?? "",
  };
}

export function buildAiHubTaggingResultPath(
  resultId: string,
  query?: TaggingResultDetailRouteQuery,
): string {
  const path = `/ai-hub/tagging-result/${encodeURIComponent(resultId)}`;
  return query ? `${path}${buildTaggingResultDetailSearch(query)}` : path;
}

export function buildAiHubConditionalTaggingResultPath(
  resultId: string,
  query?: TaggingResultDetailRouteQuery,
): string {
  const path = `/ai-hub/conditional-tagging-result/${encodeURIComponent(resultId)}`;
  return query ? `${path}${buildTaggingResultDetailSearch(query)}` : path;
}

export function buildAiHubObjectAnalysisPath(assessmentId: string): string {
  return `/ai-hub/object-analysis/${encodeURIComponent(assessmentId)}`;
}

export function buildAiHubTriageResultPath(resultId: string): string {
  return `/ai-hub/triage-result/${encodeURIComponent(resultId)}`;
}

export type AiHubTabKey =
  | "taggingResult"
  | "conditionalTaggingResult"
  | "objectToObjectAnalysisResults"
  | "implementationRecords"
  | "triageResult";

export const AI_HUB_TAB_LABELS: Record<AiHubTabKey, string> = {
  taggingResult: "Classification Result",
  conditionalTaggingResult: "Conditional Classification Result",
  objectToObjectAnalysisResults: "Object to Object Results",
  implementationRecords: "Executed Assessments",
  triageResult: "Triage Results",
};

export function isAiHubTabKey(
  value: string | null | undefined,
): value is AiHubTabKey {
  return (
    value === "taggingResult" ||
    value === "conditionalTaggingResult" ||
    value === "objectToObjectAnalysisResults" ||
    value === "implementationRecords" ||
    value === "triageResult"
  );
}

export type AssessmentPackRouteQuery = {
  referenceObjectType: string;
  referenceObjectId: string;
  referenceObjectVersion: string;
};

export function readAssessmentPackQuery(
  searchParams: URLSearchParams,
): AssessmentPackRouteQuery {
  return {
    referenceObjectType: searchParams.get("reference_object_type")?.trim() ?? "",
    referenceObjectId: searchParams.get("reference_object_id")?.trim() ?? "",
    referenceObjectVersion:
      searchParams.get("reference_object_version")?.trim() ?? "",
  };
}

export function buildAiHubAssessmentPackPath(
  query: AssessmentPackRouteQuery,
): string {
  const params = new URLSearchParams();
  const type = query.referenceObjectType.trim();
  const id = query.referenceObjectId.trim();
  const version = query.referenceObjectVersion.trim();
  if (type) params.set("reference_object_type", type);
  if (id) params.set("reference_object_id", id);
  if (version) params.set("reference_object_version", version);
  const qs = params.toString();
  return qs ? `${AI_HUB_ASSESSMENT_PACK_PATH}?${qs}` : AI_HUB_ASSESSMENT_PACK_PATH;
}

/** Unique grid/selection key when the same object_id has multiple versions. */
export function buildTaggingResultRowId(
  objectId: string,
  version: string,
): string {
  const id = objectId.trim();
  const ver = version.trim();
  return ver ? `${id}::${ver}` : id;
}

/** View-model row for the Tagging Result grid. */
export type TaggingResultRow = {
  id: string;
  objectId: string;
  objectType: string;
  objectTypeName: string;
  objectSubType: string;
  objectSubTypeName: string;
  /** Optional concept codes used for local filter matching. */
  conceptCodes: string[];
  title: string;
  version: string;
  /** concept_name → joined `values[].name` from the search payload. */
  tagsByConcept: Record<string, string>;
  /** Object detail URL from the search item `object_id_link` field. */
  link?: string;
};

export const AI_HUB_DEFAULT_PAGE_SIZE = 10;

/** Default max rows a user can check in AI Hub result lists. */
export const AI_HUB_MAX_ROW_SELECTION = 10;

export function aiHubRowSelectionLimitMessage(
  maxSelection = AI_HUB_MAX_ROW_SELECTION,
): string {
  return `You can select up to ${maxSelection} rows.`;
}
